/**
 * ROARDRIVE legal texts: same document set and structure as E-METAR (Fremmenn Dynamics, S.L.),
 * adapted to what ROARDRIVE actually does: an in-car engine-sound app (web app / PWA) that reads
 * speed from GPS or, with the optional CAN bridge, from the car, and sells engines as one-off purchases.
 */
export type LegalDocumentId = 'privacy' | 'terms' | 'cookies' | 'gdpr-rights' | 'legal-notice' | 'delete-account';

export interface LegalSection {
  heading: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface LegalDocument {
  title: string;
  updatedAt: string;
  intro: string;
  sections: LegalSection[];
}

export const legalDocumentOrder: LegalDocumentId[] = ['privacy', 'terms', 'cookies', 'gdpr-rights', 'legal-notice', 'delete-account'];

export const legalLabels: Record<LegalDocumentId, string> = {
  privacy: 'Privacy Policy',
  terms: 'Terms of Use',
  cookies: 'Cookie Policy',
  'gdpr-rights': 'GDPR Rights',
  'legal-notice': 'Legal Notice',
  'delete-account': 'Account Deletion',
};

export const COMPANY = {
  name: 'Fremmenn Dynamics, S.L.',
  taxId: 'B75342105',
  address: 'Avda. Catedral, Núm. 6, Planta 1, 08002 Barcelona, Spain',
  email: 'info@fremmenndynamics.com',
  privacyEmail: 'privacy@roardrive.com',
  website: 'https://roardrive.com',
};

export const companyDetails: string[] = [
  `Controller: ${COMPANY.name}`,
  `Tax ID: ${COMPANY.taxId}`,
  `Registered office: ${COMPANY.address}`,
  `Main contact: ${COMPANY.email}`,
  `Privacy contact: ${COMPANY.privacyEmail}`,
  `Website: ${COMPANY.website}`,
];

export function isLegalDocumentId(x: string): x is LegalDocumentId {
  return (legalDocumentOrder as string[]).includes(x);
}

const UPDATED = 'Last updated: August 29, 2026';

export const legalDocuments: Record<LegalDocumentId, LegalDocument> = {
  privacy: {
    title: 'Privacy Notice',
    updatedAt: UPDATED,
    intro: `Data Controller: ${COMPANY.name}, Barcelona, Spain. Contact: ${COMPANY.privacyEmail}`,
    sections: [
      {
        heading: '1. Overview',
        paragraphs: [
          `ROARDRIVE is an in-car engine-sound experience built and operated by ${COMPANY.name}. It runs as a web app in your car's browser or on your phone, and reproduces licensed engine recordings that follow your real speed. We respect your privacy and handle your data transparently. This notice explains what data we collect, why, and how we use it across roardrive.com and the ROARDRIVE companion app.`,
        ],
      },
      {
        heading: '2. Data We Collect',
        paragraphs: ['When you use ROARDRIVE, we collect the following categories of data:'],
        bullets: [
          'Account data: your email address and a unique user identifier. An account is needed to buy an engine and optional for everything else; it is what makes your purchases and settings follow you to any car. We authenticate with a one-time code sent by email, so there is no password.',
          'Device data: a random device identifier stored in your browser, plus browser/OS version and screen format, used to link purchases to the car you bought them in and to deliver the right layout.',
          'Purchase data: which engines you bought and the transaction identifiers provided by our payment processor (Stripe, or Apple for purchases made inside the iPhone app). We do not store your card number, bank details or CVV: payment information is handled directly by the payment provider.',
          'Settings: your chosen top speed, speed units, gearbox mode, volume and similar preferences, so they are the same in every car when you sign in.',
          'Driving data: speed (from GPS or, if you connect the optional CAN bridge, from the car itself: speed, accelerator, brake, gear, and body signals such as doors, lights and wipers). This data is processed live to drive the sound and is not stored, except as described below.',
          'Location data: with the GPS input, your position is used on the device to derive speed and, if you are on a supported circuit, to time laps. Positions are not sent to our servers; only completed lap times and the circuit identifier are, if you use the lap timer.',
          'Crash and diagnostic data: technical information about errors and audio performance, used exclusively to keep the app stable.',
        ],
      },
      {
        heading: '3. Why We Collect This Data',
        paragraphs: ['We use your data only for the following purposes:'],
        bullets: [
          'To provide the ROARDRIVE service (authentication, purchases, delivering the engines you own to your devices).',
          'To make the experience follow you (settings and purchases on every car you sign in to).',
          'To improve the product (which engines and features are used, where the audio struggles).',
          'To maintain reliability and security (crash reporting, fraud and abuse prevention).',
          'To communicate with you about your account, purchases and service updates.',
        ],
      },
      {
        heading: '4. What We Do Not Do',
        paragraphs: [
          'We want to be explicit about what we do not do with your data:',
          'Our analytics are strictly first-party: data collected within ROARDRIVE is used by ROARDRIVE, on our own infrastructure, to operate and improve the service.',
        ],
        bullets: [
          'We do not sell your data. Ever.',
          'We do not share your data with data brokers.',
          'We do not use your data to serve personalized advertising.',
          'We do not build location histories: GPS positions stay on your device.',
          'We do not link your activity in ROARDRIVE with data from third-party apps or websites for advertising or advertising measurement.',
        ],
      },
      {
        heading: '5. Third-Party Service Providers',
        paragraphs: [
          'We use a limited set of third-party providers strictly necessary to operate the service. Each provider acts as a data processor under our instructions:',
          'We do not use third-party advertising networks, attribution SDKs, or marketing trackers.',
        ],
        bullets: [
          'Cloud hosting and database providers (for backend operations and account storage).',
          'Email delivery (for one-time sign-in codes and purchase receipts).',
          'Payment processing (Stripe).',
          'Crash reporting (for diagnostics).',
        ],
      },
      {
        heading: '6. Data Retention',
        paragraphs: [
          'We retain your account data for as long as your account is active. If you delete your account, we delete or anonymize your personal data within 30 days, except where retention is required by law (for example, billing records under tax regulations).',
        ],
      },
      {
        heading: '7. Your Rights',
        paragraphs: [
          'Under the EU General Data Protection Regulation (GDPR) and equivalent regulations, you have the right to:',
          `To exercise any of these rights, contact us at ${COMPANY.privacyEmail}. We respond within 30 days.`,
        ],
        bullets: [
          'Access the personal data we hold about you.',
          'Correct inaccurate data.',
          'Delete your data ("right to be forgotten").',
          'Export your data in a portable format.',
          'Object to or restrict certain processing.',
          'Withdraw consent where processing is based on consent.',
        ],
      },
      {
        heading: '8. International Transfers',
        paragraphs: [
          'If your data is processed outside the European Economic Area, we ensure that appropriate safeguards are in place (Standard Contractual Clauses or equivalent mechanisms) to protect your data.',
        ],
      },
      {
        heading: '9. Children',
        paragraphs: ['ROARDRIVE is intended for drivers. It is not directed to children under 16, and we do not knowingly collect data from children.'],
      },
      {
        heading: '10. Changes to This Notice',
        paragraphs: [
          'We may update this notice from time to time. Material changes will be communicated through the app or by email. The "Last updated" date at the top of this notice reflects the most recent revision.',
        ],
      },
      {
        heading: '11. Contact',
        paragraphs: [
          'For any privacy-related question, request, or complaint:',
          `${COMPANY.name} Barcelona, Spain. ${COMPANY.privacyEmail}`,
          'You also have the right to lodge a complaint with the Spanish Data Protection Agency (Agencia Española de Protección de Datos, www.aepd.es) or the data protection authority in your country of residence.',
        ],
      },
    ],
  },

  terms: {
    title: 'Terms of Use',
    updatedAt: UPDATED,
    intro: 'These terms govern access to and use of ROARDRIVE and any service, content or feature offered through roardrive.com and the ROARDRIVE companion app.',
    sections: [
      {
        heading: '1. Service scope',
        paragraphs: [
          'ROARDRIVE reproduces engine sounds inside your car that follow your real speed, with a virtual gearbox and race-dash display. It is an entertainment and immersion product. It does not read, modify or control any driving function of your vehicle.',
        ],
      },
      {
        heading: '2. Safe use',
        bullets: [
          'ROARDRIVE is played through the car\'s own audio system or a device you choose. You are responsible for keeping the volume at a level that lets you hear the road, other vehicles and emergency services at all times.',
          'Never operate the app while driving. Set it up before you move; the interface is designed to need no interaction once you drive.',
          'ROARDRIVE does not alter your car\'s behaviour. Optional inputs such as the CAN bridge only read signals; use them in accordance with your vehicle manufacturer\'s terms and local law.',
          'Respect speed limits and traffic law. The virtual revs, gears and lap timer are entertainment, not an invitation to drive faster.',
          'Racing, timed laps and driving at the limit belong on a closed circuit or private track, never on a public road. The lap timer is built for the track and for nothing else.',
          'What you do at the wheel is your decision and your responsibility. An engine sound changes nothing about the road, the grip of your tyres, the distance you need to stop or the law that applies to you.',
        ],
      },
      {
        heading: '3. Account requirements',
        bullets: [
          'An account is required to buy an engine, and optional for everything else: the simulator and the demo run without one. You must provide an email address you control; signing in and signing up are the same step, a one-time code sent to that address, and there is no password to remember or lose.',
          'You are responsible for all activity carried out through your account and the devices linked to it.',
          'You may not share access fraudulently or create false or automated accounts.',
        ],
      },
      {
        heading: '4. Permitted use',
        bullets: [
          'Use the service in accordance with law, good faith and these terms.',
          'Do not attempt to access systems, APIs, third-party accounts or restricted areas without authorization.',
          'Do not extract, copy, redistribute or commercially exploit the audio recordings, images or other content delivered by the service. Engines are licensed to you for playback within ROARDRIVE only.',
          'Do not interfere with the stability, security or performance of the platform.',
        ],
      },
      {
        heading: '5. Purchases, demo and payments',
        paragraphs: [
          'Engines can be tried in demo mode (limited speed and time) and bought as one-off purchases. The price shown at checkout forms part of the applicable contract at that time. A purchase belongs to the account it was made from, so the engine is yours in every car and on every screen you sign in to, and it survives a new phone or a reinstall. Purchases made inside the iPhone app go through Apple and are restored through Apple, with the same effect.',
          'Digital content is delivered immediately after purchase. By completing the purchase you agree that delivery starts at once and acknowledge that the statutory right of withdrawal for digital content is lost once delivery has begun, without prejudice to any mandatory consumer rights.',
        ],
      },
      {
        heading: '6. Intellectual property and licensed multimedia',
        paragraphs: [
          `The software, design, brand, texts, databases and distinctive elements of ROARDRIVE are owned by ${COMPANY.name} or its licensors. Use of the service does not transfer any intellectual property rights beyond the limited, personal, non-transferable licence needed to use it.`,
          `All engine recordings, images and other multimedia content used in ROARDRIVE are used under licences held by ${COMPANY.name}. Car makes, model names and logos that may appear in the service are trademarks of their respective owners, used only to identify the sound being reproduced; no affiliation with, endorsement by or sponsorship from any vehicle manufacturer is implied.`,
        ],
      },
      {
        heading: '7. Availability and disclaimer',
        paragraphs: [
          'We strive to keep the service available, but we do not guarantee continuous availability, complete absence of errors or fitness for a particular purpose. Audio may depend on your car\'s browser, connectivity and device capabilities. The speed, revs, gear and lap-time information shown is derived from GPS or vehicle signals and is indicative only; it must never be relied on as a speedometer or for any driving decision.',
        ],
      },
      {
        heading: '8. Limitation of liability',
        paragraphs: [
          'To the maximum extent permitted by law, we are not liable for indirect damages, loss of profits, fines, accidents or any consequence of driving decisions taken while using the service. Nothing here limits liability that cannot legally be excluded, including liability for wilful misconduct or gross negligence.',
        ],
      },
      {
        heading: '9. Suspension, deactivation and termination',
        paragraphs: [
          'We may suspend or cancel accounts and purchases where there is a breach of these terms, fraud, abusive use (including extraction of licensed content) or risk to the security of the service. Users may also request account deletion through the tools provided or by contacting support.',
        ],
      },
      {
        heading: '10. Governing law and jurisdiction',
        paragraphs: ['These terms are governed by Spanish law. Unless mandatory consumer law provides otherwise, the parties submit to the courts of Barcelona, Spain.'],
      },
    ],
  },

  cookies: {
    title: 'Cookie Policy',
    updatedAt: UPDATED,
    intro: 'This policy explains which cookies and similar technologies ROARDRIVE may use on roardrive.com, for what purposes and how you can manage them.',
    sections: [
      {
        heading: '1. What cookies are',
        paragraphs: [
          'Cookies and similar technologies (such as browser local storage) are small pieces of data stored in your browser when you visit a website. They are used to remember preferences, maintain sessions and keep the service working.',
        ],
      },
      {
        heading: '2. What we use',
        bullets: [
          'Strictly necessary storage: a random device identifier, your sign-in session, the engines you own and a cached copy of the audio so the app works offline in the car. Without these the service cannot function.',
          'Preference storage: top speed, speed units, gearbox mode, volume, relay room and similar choices.',
          'Payment cookies: set by Stripe during checkout, under Stripe\'s own policy.',
          'We do not use analytics cookies from third parties, advertising cookies or marketing trackers.',
        ],
      },
      {
        heading: '3. Legal basis and consent',
        paragraphs: [
          'Necessary storage is used on the basis of legitimate interest or because it is required to deliver the service you request. Non-essential cookies, should we introduce them, will only be activated where required by law and once you have provided consent.',
        ],
      },
      {
        heading: '4. How to manage or disable cookies',
        bullets: [
          'You can configure your browser to block or delete cookies and site data that have already been stored.',
          'Clearing site data signs you out and removes the offline audio cache; your purchases are restored when you sign in again.',
          'Disabling strictly necessary storage will prevent the app from working.',
        ],
      },
      {
        heading: '5. Third-party cookies',
        paragraphs: [
          'When we use third-party services for payments or authentication, those providers may place their own cookies under their own policies. We recommend reviewing those external service policies as well.',
        ],
      },
    ],
  },

  'gdpr-rights': {
    title: 'GDPR Rights',
    updatedAt: UPDATED,
    intro: 'If you reside in the European Union or the General Data Protection Regulation applies to you, you may exercise the following rights regarding your personal data processed by ROARDRIVE.',
    sections: [
      { heading: '1. Right of access', paragraphs: ['You may request confirmation as to whether we process your personal data and obtain a copy of the information and the purposes for which it is processed.'] },
      { heading: '2. Right to rectification', paragraphs: ['You may ask us to correct inaccurate or incomplete data. Where possible, you can also keep part of your information up to date from your own account.'] },
      { heading: '3. Right to erasure', paragraphs: ['You may request deletion of your data when it is no longer necessary, you withdraw consent and there is no other legal basis, or the processing is unlawful. This right may be subject to legal limitations.'] },
      { heading: '4. Right to restriction and objection', paragraphs: ['You may ask us to temporarily restrict certain processing activities or object to processing based on legitimate interest when there are grounds related to your particular situation.'] },
      { heading: '5. Right to portability', paragraphs: ['Where processing is based on consent or contract and carried out by automated means, you may request to receive your data in a structured format and, where technically feasible, transmit it to another controller.'] },
      {
        heading: '6. How to exercise these rights',
        paragraphs: [
          `You can exercise your rights by writing to ${COMPANY.privacyEmail} and including your request, the affected account and any information needed to verify your identity. We will respond within the legal deadlines that apply.`,
          'If you believe you have not received an adequate response, you may contact the competent supervisory authority (in Spain, the Agencia Española de Protección de Datos, www.aepd.es).',
        ],
      },
    ],
  },

  'legal-notice': {
    title: 'Legal Notice',
    updatedAt: UPDATED,
    intro: 'This legal notice governs access to, browsing of and use of the roardrive.com website and app.',
    sections: [
      { heading: '1. Website ownership', paragraphs: [`The website roardrive.com and the ROARDRIVE app are owned by ${COMPANY.name}, with Tax ID ${COMPANY.taxId} and registered office at ${COMPANY.address}.`] },
      { heading: '2. Conditions of access and use', paragraphs: ['Accessing the website implies acceptance of this legal notice and the rest of the applicable legal texts. The user agrees to use the website diligently, lawfully and with respect for third-party rights.'] },
      {
        heading: '3. Intellectual and industrial property',
        paragraphs: [
          `All contents, code, designs, logos, trademarks and other site elements are protected by intellectual and industrial property rights. Engine recordings, images and other multimedia content are used under licences held by ${COMPANY.name}. Their reproduction or use beyond what is permitted by law or expressly authorized is prohibited.`,
          'Vehicle makes, model names and logos are trademarks of their respective owners and are used solely to identify the sound reproduced; no affiliation, endorsement or sponsorship is implied.',
        ],
      },
      { heading: '4. Links and third-party content', paragraphs: ['The website may include links to third-party services. We do not control their content and do not assume responsibility for their availability, legality or policies. Access to those external sites is at the user\'s own risk.'] },
      { heading: '5. Liability', paragraphs: [`${COMPANY.name} does not guarantee the absence of interruptions or errors and is not liable for damages arising from use of the site beyond what is legally required. We reserve the right to modify, update or remove content and features without prior notice.`] },
      { heading: '6. Contact', paragraphs: [`For any inquiry related to this website you can contact ${COMPANY.email}. For privacy matters, use ${COMPANY.privacyEmail}.`] },
    ],
  },

  'delete-account': {
    title: 'Account and Data Deletion',
    updatedAt: UPDATED,
    intro: `How to delete your ROARDRIVE account and the personal data associated with it. ROARDRIVE is operated by ${COMPANY.name} (Barcelona, Spain).`,
    sections: [
      { heading: '1. Who this applies to', paragraphs: ['This page covers users of ROARDRIVE on any platform: the web app in your car, the phone companion app and roardrive.com. They share the same account.'] },
      {
        heading: '2. How to request deletion',
        paragraphs: [
          `Write to ${COMPANY.privacyEmail} from the email address associated with your account and include the words "delete my account" in the subject. We will verify your identity and process the deletion within 30 days.`,
          'If you have lost access to your registered email, contact us at the same address and we will guide you through identity verification.',
          'To simply sign out of a car without deleting anything, use the ✕ next to your email on the home screen.',
        ],
      },
      {
        heading: '3. What data is deleted',
        paragraphs: ['When you delete your account, the following personal data is deleted or anonymized within 30 days:'],
        bullets: [
          'Your email address (anonymized immediately, hard-deleted within 30 days).',
          'Your unique user identifier and the devices linked to your account.',
          'Your settings (top speed, units, gearbox, volume, relay room).',
          'Your lap times.',
          'Crash and diagnostic records linked to your account.',
        ],
      },
      {
        heading: '4. What data is retained, and for how long',
        paragraphs: ['A small subset of data must be kept after deletion to comply with legal obligations. We keep only what the law strictly requires, and only for the legally required period:'],
        bullets: [
          'Billing and tax records (transaction identifiers, purchase history) are retained for the period required by Spanish tax law, generally up to 6 years. These records are no longer linked to a usable account.',
          'Aggregated, non-identifying usage statistics may be retained indefinitely. These cannot be linked back to you.',
          'Backup snapshots may contain residual copies of your data for up to 35 days after deletion before they are overwritten on rotation.',
        ],
      },
      { heading: '5. Purchases', paragraphs: ['Engines are one-off purchases. Deleting your account removes the link between those purchases and you; they cannot be restored to a new account afterwards. If you only want to use them in another car, sign in there instead of deleting.'] },
      { heading: '6. Effect of deletion', paragraphs: ['Once your account is deleted you will no longer be able to sign in with the same credentials. You can create a new ROARDRIVE account in the future with the same email address; it will start empty and will not be linked to any previous data.'] },
      { heading: '7. Contact', paragraphs: [`For any question related to account or data deletion, contact ${COMPANY.privacyEmail}. For general inquiries about the company, contact ${COMPANY.email}.`] },
    ],
  },
};
